import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { YouTubeSearchResult, DrillFromYouTube, YouTubeSortOrder } from './Model';
import { restClient } from './RestClient';
import { AuthService } from './AuthService';
import { YouTubeSearchResultCard } from './YouTubeSearchResultCard';
import { LoadingButton } from './LoadingButton';
import { useReferenceData } from './ReferenceDataContext';

interface YouTubeSearchProps {
  onError?: (message: string) => void;
}

const SORT_OPTIONS: { value: YouTubeSortOrder; label: string }[] = [
  { value: 'combined', label: 'Combined Score' },
  { value: 'views', label: 'Most Views' },
  { value: 'likes', label: 'Most Likes' },
  { value: 'comments', label: 'Most Comments' },
];

const MAX_RESULTS_OPTIONS = [10, 25, 50];

export const YouTubeSearch: React.FC<YouTubeSearchProps> = ({ onError }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<YouTubeSortOrder>('combined');
  const [maxResults, setMaxResults] = useState<number>(25);
  const [results, setResults] = useState<YouTubeSearchResult[]>([]);
  const [selectedVideos, setSelectedVideos] = useState<Map<string, DrillFromYouTube>>(new Map());
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const { currentSport } = useReferenceData();
  const isAdmin = AuthService.isAdmin();

  useEffect(() => {
    if (!isAdmin) {
      navigate('/training-drills');
    }
  }, [isAdmin, navigate]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      onError?.('Please enter a search term');
      return;
    }

    setIsSearching(true);
    setHasSearched(true);
    setSelectedVideos(new Map());

    try {
      const result = await restClient.searchYouTube({
        query: searchQuery.trim(),
        maxResults,
        sortOrder,
        sport: currentSport,
      });

      if (result.IsSuccess) {
        setResults(result.Value.results);
      } else {
        onError?.(result.Error?.description ?? 'Failed to search YouTube');
        setResults([]);
      }
    } catch (error) {
      onError?.('An unexpected error occurred while searching');
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleToggleSelection = (result: YouTubeSearchResult) => {
    const newSelected = new Map(selectedVideos);

    if (newSelected.has(result.videoId)) {
      newSelected.delete(result.videoId);
    } else {
      newSelected.set(result.videoId, {
        videoId: result.videoId,
        title: result.title,
        description: result.description,
        youtubeUrl: result.youtubeUrl,
        duration: result.duration,
        difficulty: result.inferredDifficulty,
        category: result.inferredCategory,
        skills: result.inferredSkills,
        ageGroups: result.inferredAgeGroups,
        playersRequired: result.inferredPlayersRequired,
        equipment: result.inferredEquipment,
        spaceRequired: { width: 25, height: 25 },
      });
    }

    setSelectedVideos(newSelected);
  };

  const handleUpdateSelection = (videoId: string, updates: Partial<DrillFromYouTube>) => {
    const newSelected = new Map(selectedVideos);
    const existing = newSelected.get(videoId);
    if (existing) {
      newSelected.set(videoId, { ...existing, ...updates });
      setSelectedVideos(newSelected);
    }
  };

  const handleBulkSave = async () => {
    if (selectedVideos.size === 0) {
      onError?.('Please select at least one video');
      return;
    }

    // Validate all selected videos have at least one age group
    const invalidVideos = Array.from(selectedVideos.values()).filter(
      (v) => v.ageGroups.length === 0
    );
    if (invalidVideos.length > 0) {
      onError?.(`${invalidVideos.length} video(s) need at least one age group selected`);
      return;
    }

    setIsSaving(true);

    try {
      const result = await restClient.bulkCreateDrills({
        drills: Array.from(selectedVideos.values()),
      });

      if (result.IsSuccess) {
        const { successCount, failureCount, errors } = result.Value;

        if (failureCount > 0 && errors.length > 0) {
          onError?.(
            `Created ${successCount} drill${successCount === 1 ? '' : 's'}. ` +
            `${failureCount} failed: ${errors.join(', ')}`
          );
        }

        if (successCount > 0) {
          navigate('/training-drills', {
            state: { message: `Successfully created ${successCount} drill${successCount === 1 ? '' : 's'}` },
          });
        }
      } else {
        onError?.(result.Error?.description ?? 'Failed to create drills');
      }
    } catch (error) {
      onError?.('An unexpected error occurred while saving');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSortChange = (newSort: YouTubeSortOrder) => {
    setSortOrder(newSort);
    // Re-sort existing results client-side
    if (results.length > 0) {
      const sorted = [...results].sort((a, b) => {
        switch (newSort) {
          case 'views':
            return b.viewCount - a.viewCount;
          case 'likes':
            return b.likeCount - a.likeCount;
          case 'comments':
            return b.commentCount - a.commentCount;
          default:
            return b.combinedScore - a.combinedScore;
        }
      });
      setResults(sorted);
    }
  };

  return (
    <div className="youtube-search-page">
      {/* Header */}
      <div className="youtube-search-header">
        <button onClick={() => navigate('/training-drills')} className="btn btn-secondary">
          Back to Drills
        </button>
        <h1 className="youtube-search-header-title">Search YouTube</h1>
      </div>

      {/* Search bar */}
      <div className="youtube-search-bar">
        <input
          type="text"
          placeholder="Search for training drills (e.g., passing drill, dribbling, shooting)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={handleKeyPress}
          className="youtube-search-input"
        />

        <select
          value={sortOrder}
          onChange={(e) => handleSortChange(e.target.value as YouTubeSortOrder)}
          aria-label="Sort order"
          className="youtube-search-select"
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <select
          value={maxResults}
          onChange={(e) => setMaxResults(Number(e.target.value))}
          aria-label="Maximum number of results"
          className="youtube-search-select"
        >
          {MAX_RESULTS_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option} results
            </option>
          ))}
        </select>

        <LoadingButton
          onClick={handleSearch}
          isLoading={isSearching}
          loadingText="Searching..."
          className="btn-youtube-search"
        >
          Search YouTube
        </LoadingButton>
      </div>

      {/* Results */}
      {isSearching ? (
        <div className="youtube-loading">
          <div className="youtube-spinner" />
          <p>Searching YouTube for training drills...</p>
        </div>
      ) : results.length > 0 ? (
        <>
          <div className="youtube-results-header">
            <p className="youtube-results-count">
              Found {results.length} videos. Select videos to add as drills.
            </p>
            {selectedVideos.size > 0 && (
              <span className="youtube-selected-badge">
                {selectedVideos.size} selected
              </span>
            )}
          </div>

          <div className="youtube-results-grid">
            {results.map((result) => (
              <YouTubeSearchResultCard
                key={result.videoId}
                result={result}
                isSelected={selectedVideos.has(result.videoId)}
                selectedData={selectedVideos.get(result.videoId)}
                onToggleSelection={handleToggleSelection}
                onUpdateSelection={handleUpdateSelection}
              />
            ))}
          </div>
        </>
      ) : hasSearched ? (
        <div className="youtube-empty-state">
          <p className="youtube-empty-title">No videos found</p>
          <p className="youtube-empty-subtitle">Try different search terms like "passing drill" or "dribbling practice"</p>
        </div>
      ) : (
        <div className="youtube-empty-state">
          <p className="youtube-empty-title">Search for YouTube training videos</p>
          <p className="youtube-empty-subtitle">
            Enter a search term above to find {currentSport === 'Rugby' ? 'rugby' : 'football'} training drills. You can then select videos and save them as drills.
          </p>
        </div>
      )}

      {/* Floating save bar */}
      {selectedVideos.size > 0 && (
        <div className="youtube-save-bar">
          <span className="youtube-save-bar-text">
            {selectedVideos.size} video{selectedVideos.size > 1 ? 's' : ''} selected
          </span>
          <LoadingButton
            onClick={handleBulkSave}
            isLoading={isSaving}
            loadingText="Saving..."
            className="btn-youtube-save"
          >
            Save as Drills
          </LoadingButton>
          <button onClick={() => setSelectedVideos(new Map())} className="btn-youtube-clear">
            Clear
          </button>
        </div>
      )}
    </div>
  );
};
